import type { EtfRow, EtfScreenerResponse } from "./etf-screener.type";

/** A trimmed non-empty string, or null. */
export const str = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

/** A finite number (numeric strings accepted), or null. */
export const num = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Where a fund trades, from its ticker suffix. Never from listing_country,
 * which is the domicile. An unknown suffix is null, never defaulted to US.
 */
export const etfVenueOf = (ticker: string): EtfRow["venue"] => {
  const upper = ticker.toUpperCase();
  if (upper.endsWith(".L")) return "GB";
  if (upper.endsWith(".TO")) return "CA";
  return upper.includes(".") ? null : "US";
};

/** One row from the gateway's 32-key shape, walking only declared keys. */
export const normalizeEtfRow = (raw: unknown): EtfRow | null => {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const ticker = str(r.ticker);
  if (!ticker) return null;
  return {
    ticker,
    fundName: str(r.fund_name),
    issuer: str(r.issuer),
    listingCountry: str(r.listing_country),
    venue: etfVenueOf(ticker),
    reportingCurrency: str(r.reporting_currency),
    expenseRatio: num(r.expense_ratio),
    aum: num(r.aum),
    status: str(r.status),
    statusDjim: str(r.halal_status_djim),
    statusFtse: str(r.halal_status_ftse),
    statusMsci: str(r.halal_status_msci),
    statusSp: str(r.halal_status_sp),
    compliantWeightPerc: num(r.compliant_weight_perc),
    nonCompliantWeightPerc: num(r.non_compliant_weight_perc),
    doubtfulWeightPerc: num(r.doubtful_weight_perc),
    unscreenedWeightPerc: num(r.unscreened_weight_perc),
    coverageWeightPerc: num(r.coverage_weight_perc),
    holdingsTotal: num(r.holdings_total),
    holdingsScreened: num(r.holdings_screened),
    holdingsAsOf: str(r.holdings_as_of),
    lastUpdated: str(r.last_updated),
  };
};

/** The gateway's `{etfs, total, limit, offset}`; limit is read back, not assumed. */
export const normalizeEtfScreenerResponse = (
  raw: unknown,
  requestedLimit: number,
): EtfScreenerResponse => {
  const body = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const rows = Array.isArray(body.etfs) ? body.etfs : [];
  const data = rows
    .map(normalizeEtfRow)
    .filter((row): row is EtfRow => row !== null);
  return {
    data,
    total: Math.max(0, num(body.total) ?? data.length),
    limit: Math.max(1, num(body.limit) ?? requestedLimit),
    offset: Math.max(0, num(body.offset) ?? 0),
  };
};
